// Rebuilt right panel — single filterable list of all five entity types
// (personnel / equipment / location / access point / chamber) with a
// unified EntityCard. Replaces the four-section panel.
//
// State model:
//   - panelFilter: local React state (which entity type the list shows)
//   - statusFilter: local React state (only personnel/equipment use it)
//   - rtls.filterLevel: Redux (in-scene markers also read this for level
//     filtering, so it stays in the slice)
//
// Dialogs are mounted once at the bottom; the per-row pencil opens the
// right one based on the active filter.

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import clsx from 'clsx'
import {ChevronDown} from 'lucide-react'
import {useVirtualizer} from '@tanstack/react-virtual'
import {useAppDispatch} from '../app/useAppDispatch'
import {useAppSelector} from '../app/useAppSelector'
import {rtlsActions} from '@shared/slices/rtlsSlice'
import type {AssetStatus, FilterLevel} from '@shared/types/rtlsTypes'

import {ConnectionBanner} from './rtls/ConnectionBanner'
import {EntityCard, type EntityKind} from './rtls/EntityCard'
import {useEntityListForFilter, type PanelFilter} from './rtls/useEntityListForFilter'
import {EditAssetDialog} from './rtls/EditAssetDialog'
import {AddLocationDialog} from './rtls/AddLocationDialog'
import {AddAccessPointDialog} from './rtls/AddAccessPointDialog'

const PANEL_FILTERS: {key: PanelFilter; label: string}[] = [
  {key: 'all',          label: 'All'},
  {key: 'personnel',    label: 'Personnel'},
  {key: 'equipment',    label: 'Equipment'},
  {key: 'location',     label: 'Locations'},
  {key: 'access_point', label: 'Access points'},
  {key: 'chamber',      label: 'Chambers'},
]

const STATUS_FILTERS: {key: 'all' | AssetStatus; label: string}[] = [
  {key: 'all',     label: 'All'},
  {key: 'active',  label: 'Active'},
  {key: 'alert',   label: 'Alert'},
  {key: 'offline', label: 'Offline'},
]

export const RTLSPanel: React.FC = () => {
  const dispatch = useAppDispatch()

  // ── Panel state ─────────────────────────────────────────────────────
  // panelFilter is in Redux so the 3D scene markers can also gate on it
  // (only the active type renders on the map).
  const panelFilter = useAppSelector(s => s.rtls.panelFilter) as PanelFilter
  const setPanelFilter = useCallback(
    (v: PanelFilter) => dispatch(rtlsActions.setPanelFilter(v)),
    [dispatch],
  )
  const [statusFilter, setStatusFilter] = useState<'all' | AssetStatus>('all')

  // Level filter stays in Redux so the in-scene markers honor it too.
  const levelFilter = useAppSelector(s => s.rtls.filterLevel)
  const setLevelFilter = useCallback(
    (lv: FilterLevel) => dispatch(rtlsActions.setFilterLevel(lv)),
    [dispatch],
  )

  // Stats now come from the active filter (returned by the hook below).

  // ── Level dropdown options — derived from assets so only levels in use
  //    appear in the menu. (Markers also use the asset level set.) ────
  const assetsRecord = useAppSelector(s => s.rtls.assets)
  const levelOptions = useMemo<FilterLevel[]>(() => {
    const set = new Set<number>()
    for (const a of Object.values(assetsRecord)) {
      if (typeof a.level === 'number') set.add(a.level)
    }
    return ['all', ...Array.from(set).sort((a, b) => a - b)]
  }, [assetsRecord])

  // ── Dialog state. `editing` opens an edit dialog from a per-row ✎.
  //    `adding` opens an add dialog from the section-header + icon.
  //    Both share the same dialog components in their respective modes.
  const [editing, setEditing] = useState<{kind: EntityKind; id: string} | null>(null)
  const [adding,  setAdding]  = useState<'location' | 'access_point' | null>(null)
  const handleEdit = useCallback((kind: EntityKind, id: string) => {
    setEditing({kind, id})
  }, [])
  const closeEdit = useCallback(() => setEditing(null), [])
  const closeAdd  = useCallback(() => setAdding(null), [])

  // ── Derive the list to render ──────────────────────────────────────
  const {
    entities,
    visibleCount,
    headerLabel,
    supportsStatusFilter,
    addsTo,
    stats,
  } = useEntityListForFilter({
    panelFilter,
    levelFilter,
    statusFilter,
    onEdit: handleEdit,
  })

  // When the active filter doesn't support status (loc/AP/chamber), reset
  // the chip so re-selecting Personnel doesn't surprise the user with a
  // stale status filter applied.
  useEffect(() => {
    if (!supportsStatusFilter && statusFilter !== 'all') setStatusFilter('all')
  }, [supportsStatusFilter, statusFilter])

  // ── Virtualized list ────────────────────────────────────────────────
  const parentRef = useRef<HTMLDivElement>(null)
  const virtualizer = useVirtualizer({
    count: entities.length,
    getScrollElement: () => parentRef.current,
    // Compact 2-line cards (~52 px tall including mb-xs spacing).
    // measureElement refines once each row mounts; this is just the
    // initial-pass estimate.
    estimateSize: () => 52,
    overscan: 5,
  })

  // Two-way binding with scene clicks: scroll the selected asset into view.
  const selectedAssetId = useAppSelector(s => s.rtls.selectedAssetId)
  useEffect(() => {
    if (!selectedAssetId) return
    const idx = entities.findIndex(e => e.id === selectedAssetId)
    if (idx >= 0) virtualizer.scrollToIndex(idx, {align: 'center'})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssetId])

  return (
    <aside
      className="flex h-full w-sidebar min-w-sidebar max-w-sidebar flex-col border-l border-surfaceBorder bg-surface"
      aria-label="RTLS tracking panel"
    >
      {/* Header */}
      <header className="flex h-panel-header items-center justify-between border-b border-[#404040]/60 px-lg">
        <span className="text-xl font-semibold text-textPrimary">RTLS Tracking</span>
      </header>

      <ConnectionBanner />

      {/* Filter dropdowns — Filter By, Status (conditional), Level */}
      <div className="flex flex-col gap-md border-b border-[#404040]/60 px-lg py-md">
        <DropdownField
          label="Filter By"
          value={panelFilter}
          onChange={v => setPanelFilter(v as PanelFilter)}
        >
          {PANEL_FILTERS.map(f => (
            <option key={f.key} value={f.key}>{f.label}</option>
          ))}
        </DropdownField>

        {supportsStatusFilter && (
          <DropdownField
            label="Status"
            value={statusFilter}
            onChange={v => setStatusFilter(v as 'all' | AssetStatus)}
          >
            {STATUS_FILTERS.map(s => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </DropdownField>
        )}

        <DropdownField
          label="Level"
          value={String(levelFilter)}
          onChange={v => setLevelFilter(v === 'all' ? 'all' : Number(v))}
        >
          {levelOptions.map(l => (
            <option key={String(l)} value={String(l)}>
              {l === 'all' ? 'All levels' : `Level ${l}`}
            </option>
          ))}
        </DropdownField>
      </div>

      {/* Stat grid — recomputes per active filter (sits below Level per
          the latest design pass). Each card carries a colored left
          border that matches the semantic of its metric. */}
      <div className="grid grid-cols-2 gap-sm border-b border-[#404040]/60 bg-surface px-lg py-md">
        <Stat label="Total"   value={stats.total}                                       borderClass="border-l-primary" />
        <Stat label="Active"  value={stats.active}  valueClass="text-statusGreen"      borderClass="border-l-statusGreen" />
        <Stat label="Alert"   value={stats.alert}   valueClass="text-statusRed"        borderClass="border-l-statusRed" />
        <Stat label="Offline" value={stats.offline} valueClass="text-textMuted"        borderClass="border-l-textMuted" />
      </div>

      {/* Section header — inline "LABEL (count)" + optional + icon for
          create flows (Locations / Access Points) */}
      <div className="flex items-center justify-between border-b border-[#404040]/60 px-lg py-sm">
        <span className="text-xs font-semibold uppercase tracking-wider text-textMuted">
          {headerLabel} ({visibleCount})
        </span>
        {addsTo && (
          <button
            type="button"
            onClick={() => setAdding(addsTo)}
            title={`Add ${addsTo === 'location' ? 'location' : 'access point'}`}
            aria-label={`Add ${addsTo === 'location' ? 'location' : 'access point'}`}
            className="flex h-6 w-6 items-center justify-center rounded-sm text-textMuted hover:bg-surfaceCard hover:text-primary"
          >
            <span className="text-base leading-none">+</span>
          </button>
        )}
      </div>

      {/* Virtualized list */}
      <div
        ref={parentRef}
        className="flex-1 overflow-y-auto py-xs"
        role="listbox"
        aria-label={headerLabel}
      >
        {entities.length === 0 ? (
          <div className="flex h-full items-center justify-center p-lg text-sm text-textMuted">
            No matches.
          </div>
        ) : (
          <div
            style={{height: `${virtualizer.getTotalSize()}px`, position: 'relative'}}
          >
            {virtualizer.getVirtualItems().map(vitem => {
              const e = entities[vitem.index]
              return (
                <div
                  key={e.id}
                  data-index={vitem.index}
                  ref={virtualizer.measureElement}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${vitem.start}px)`,
                  }}
                >
                  <EntityCard {...e} />
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Mounted dialogs — opened by either per-row ✎ (edit mode) or the
          section-header + icon (add mode). Add* dialogs handle both modes. */}
      <EditAssetDialog
        open={editing?.kind === 'personnel' || editing?.kind === 'equipment'}
        editingId={
          editing?.kind === 'personnel' || editing?.kind === 'equipment'
            ? editing.id
            : null
        }
        onClose={closeEdit}
      />
      <AddLocationDialog
        open={adding === 'location' || editing?.kind === 'location'}
        editingId={editing?.kind === 'location' ? editing.id : null}
        onClose={() => { closeAdd(); closeEdit() }}
      />
      <AddAccessPointDialog
        open={adding === 'access_point' || editing?.kind === 'access_point'}
        editingId={editing?.kind === 'access_point' ? editing.id : null}
        onClose={() => { closeAdd(); closeEdit() }}
      />
    </aside>
  )
}

// ── Local subcomponents ────────────────────────────────────────────────

const Stat: React.FC<{
  label: string
  value: number
  valueClass?: string
  /** Tailwind class for the left-edge accent border (e.g. `border-l-statusGreen`). */
  borderClass?: string
}> = ({label, value, valueClass, borderClass}) => (
  <div
    className={clsx(
      'flex items-baseline justify-between gap-xs rounded-sm border border-surfaceBorder border-l-[4px] bg-surfaceLight px-sm py-[6px]',
      borderClass,
    )}
  >
    <div className="text-[10px] uppercase tracking-wider text-textMuted">{label}</div>
    <div className={clsx('text-lg font-semibold tabular-nums leading-none', valueClass ?? 'text-textPrimary')}>
      {value}
    </div>
  </div>
)

const DropdownField: React.FC<{
  label: string
  value: string
  onChange: (next: string) => void
  children: React.ReactNode
}> = ({label, value, onChange, children}) => (
  <label className="flex flex-col gap-xs">
    <span className="text-xs font-semibold uppercase tracking-wider text-textMuted">
      {label}
    </span>
    {/* Native <select> chevrons are pinned to the right edge by the user-
        agent stylesheet and ignore padding-right. We suppress the native
        chevron with appearance-none and render a lucide ChevronDown that
        respects layout spacing. */}
    <div className="relative">
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full appearance-none rounded-sm border border-surfaceBorder bg-surfaceLight pl-md pr-xxl py-sm text-sm text-textPrimary focus:border-primary focus:outline-none"
      >
        {children}
      </select>
      <ChevronDown
        size={16}
        className="pointer-events-none absolute right-md top-1/2 -translate-y-1/2 text-textMuted"
      />
    </div>
  </label>
)

